import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMsal } from "@azure/msal-react";
import { EventType, InteractionStatus } from "@azure/msal-browser";
import api from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Session management - using localStorage for cross-tab synchronization
const AppSession = {
  SESSION_KEY: `${window.location.hostname.replace(/\./g, "_")}_app_session`,

  isLoggedIn: () => {
    try {
      return localStorage.getItem(AppSession.SESSION_KEY) === "true";
    } catch (e) {
      return false;
    }
  },

  setLoggedIn: () => {
    try {
      localStorage.setItem(AppSession.SESSION_KEY, "true");
    } catch (e) {}
  },

  clearSession: () => {
    try {
      localStorage.removeItem(AppSession.SESSION_KEY);
    } catch (e) {}
  },
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(null);
  const [backendUser, setBackendUser] = useState(null);
  const [preferences, setPreferences] = useState({ theme: "light" });
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const avatarUrlRef = useRef(null);
  const initRef = useRef(false);
  const tokenRefreshTimerRef = useRef(null);
  const apiConfiguredRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
  }, []);

  // Validate token is for THIS app only
  const validateTokenAudience = useCallback((token) => {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return false;

      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
      );

      console.log("🔍 Token payload:", {
        aud: payload.aud,
        appid: payload.appid,
        scp: payload.scp,
        roles: payload.roles,
      });

      // Check if token audience matches our backend Application ID URI
      // Accept multiple formats that Azure AD might return
      const validAudiences = [
        "api://b0e110d8-17b7-4289-9340-9ed8576c898f/access_as_user",
        "api://b0e110d8-17b7-4289-9340-9ed8576c898f",
        "b0e110d8-17b7-4289-9340-9ed8576c898f",
      ];

      // Token audience or appid should match one of the valid formats
      const isValid =
        validAudiences.includes(payload.aud) ||
        validAudiences.includes(payload.appid);

      if (!isValid) {
        console.error("Token audience mismatch!", {
          tokenAud: payload.aud,
          tokenAppId: payload.appid,
          validAudiences,
        });
      } else {
        console.log("✅ Token audience validated successfully");
      }

      return isValid;
    } catch (e) {
      console.error("Token validation error:", e);
      return false;
    }
  }, []);

  const isTokenExpired = useCallback((account) => {
    if (!account?.idTokenClaims?.exp) return true;
    return account.idTokenClaims.exp - Math.floor(Date.now() / 1000) < 300;
  }, []);

  const handleAutoLogout = useCallback(
    async (reason) => {
      console.warn(`Auto-logout: ${reason}`);
      // Silently logout - don't show error messages to user
      AppSession.clearSession();
      setUser(null);
      setBackendUser(null);
      setAuthError(null); // Clear any existing errors
      clearTimers();

      if (avatarUrlRef.current) {
        try {
          URL.revokeObjectURL(avatarUrlRef.current);
        } catch (e) {}
        avatarUrlRef.current = null;
      }

      // Redirect to login page
      try {
        await instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
        });
      } catch (error) {
        console.error("Logout redirect failed:", error);
        // Fallback: force page reload to clear state
        window.location.href = "/";
      }
    },
    [clearTimers, instance],
  );

  const scheduleTokenRefresh = useCallback(
    (account) => {
      clearTimers();
      if (!account?.idTokenClaims?.exp) return;

      const exp = account.idTokenClaims.exp;
      const now = Math.floor(Date.now() / 1000);
      const refreshIn = Math.max((exp - now - 300) * 1000, 60000);

      console.log(`Token refresh in ${Math.floor(refreshIn / 60000)}min`);

      tokenRefreshTimerRef.current = setTimeout(async () => {
        try {
          // Refresh the backend API token
          const resp = await instance.acquireTokenSilent({
            scopes: [
              "api://b0e110d8-17b7-4289-9340-9ed8576c898f/access_as_user",
            ],
            account,
            forceRefresh: true,
          });
          if (resp?.accessToken && validateTokenAudience(resp.accessToken)) {
            console.log("Token refreshed");
            AppSession.setLoggedIn();
            scheduleTokenRefresh(account);
          } else {
            handleAutoLogout("Token validation failed");
          }
        } catch (e) {
          console.error("Refresh failed:", e);
          handleAutoLogout("Token refresh failed");
        }
      }, refreshIn);
    },
    [instance, clearTimers, validateTokenAudience, handleAutoLogout],
  );

  const getAccessToken = useCallback(async () => {
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) {
      console.log("No account available for token");
      return null;
    }

    if (isTokenExpired(account)) {
      console.warn("Token expired");
      handleAutoLogout("Token expired");
      return null;
    }

    try {
      console.log("Acquiring token for backend API...");
      const resp = await instance.acquireTokenSilent({
        scopes: ["api://b0e110d8-17b7-4289-9340-9ed8576c898f/access_as_user"],
        account,
      });

      if (!resp?.accessToken) {
        console.error("No access token in response");
        return null;
      }

      if (!validateTokenAudience(resp.accessToken)) {
        handleAutoLogout("Invalid token audience");
        return null;
      }

      console.log("Token acquired successfully");
      return resp.accessToken;
    } catch (e) {
      console.error("Token acquisition error:", e);
      if (e.errorCode === "interaction_in_progress") {
        console.log("Interaction already in progress");
        return null;
      }

      try {
        console.log("Trying SSO silent...");
        const ssoResp = await instance.ssoSilent({
          scopes: ["api://b0e110d8-17b7-4289-9340-9ed8576c898f/access_as_user"],
          loginHint: account.username,
        });

        if (!ssoResp?.accessToken) {
          console.error("No access token from SSO");
          return null;
        }

        if (!validateTokenAudience(ssoResp.accessToken)) {
          handleAutoLogout("Invalid token audience");
          return null;
        }

        console.log("SSO token acquired successfully");
        return ssoResp.accessToken;
      } catch (ssoE) {
        console.error("SSO silent failed:", ssoE);
        if (ssoE.errorCode !== "interaction_in_progress") {
          handleAutoLogout("Token acquisition failed");
        }
        return null;
      }
    }
  }, [
    instance,
    accounts,
    isTokenExpired,
    validateTokenAudience,
    handleAutoLogout,
  ]);

  const syncUserWithBackend = useCallback(
    async (azureUser) => {
      try {
        const userData = {
          user_id: azureUser.id,
          email: azureUser.email,
          display_name: azureUser.name,
          first_name: azureUser.profile?.givenName || null,
          last_name: azureUser.profile?.surname || null,
        };

        const backendUserData = await api.loginUser(userData);
        setBackendUser(backendUserData);
        if (backendUserData.preferences)
          setPreferences(backendUserData.preferences);

        // Broadcast login success to other tabs
        try {
          const channel = new BroadcastChannel("auth_channel");
          channel.postMessage({ type: "LOGIN", data: backendUserData });
          channel.close();
        } catch (e) {
          console.warn("BroadcastChannel not supported");
        }

        return backendUserData;
      } catch (e) {
        console.error("Backend sync failed:", e);
        if (e.status === 401 || e.status === 403)
          handleAutoLogout("Backend auth failed");
        return null;
      }
    },
    [handleAutoLogout],
  );

  const updatePreferences = useCallback(
    async (newPrefs) => {
      const userId = backendUser?.user_id || user?.id;
      if (!userId) return null;

      try {
        const updated = await api.updateUserPreferences(userId, newPrefs);
        setPreferences(updated);

        // Broadcast preference update to other tabs
        try {
          const channel = new BroadcastChannel("auth_channel");
          channel.postMessage({ type: "PREFERENCES_UPDATE", data: updated });
          channel.close();
        } catch (e) {
          console.warn("BroadcastChannel not supported");
        }

        return updated;
      } catch (e) {
        console.error("Update prefs failed:", e);
        return null;
      }
    },
    [backendUser, user],
  );

  // Cleanup stale flags on mount
  useEffect(() => {
    try {
      Object.keys(localStorage).forEach((k) => {
        if (
          k.includes("msal") &&
          k.includes("interaction") &&
          localStorage.getItem(k) === "interaction_in_progress"
        ) {
          console.warn("Clearing stale flag:", k);
          localStorage.removeItem(k);
        }
      });
    } catch (e) {}

    const account = instance.getActiveAccount();
    if (account && isTokenExpired(account)) {
      handleAutoLogout("Token expired on startup");
    }
  }, [instance, isTokenExpired, handleAutoLogout]);

  // CRITICAL: Configure API service with token getter as early as possible
  useEffect(() => {
    if (!apiConfiguredRef.current) {
      console.log("Configuring API service with token getter");
      api.setAccessTokenGetter(getAccessToken);
      apiConfiguredRef.current = true;
    }
  }, [getAccessToken]);

  // Cross-tab authentication and logout synchronization
  useEffect(() => {
    // Create BroadcastChannel for real-time cross-tab communication
    const channel = new BroadcastChannel("auth_channel");

    const handleStorageChange = (e) => {
      // Detect when another tab logs in or out
      if (e.key === AppSession.SESSION_KEY) {
        if (e.newValue === "true" && !user) {
          // Another tab logged in - sync this tab
          console.log("Cross-tab: Another tab logged in, syncing...");
          initRef.current = false;
          setIsLoading(true);
        } else if (e.newValue === null && user) {
          // Another tab logged out - log out this tab too
          console.log("Cross-tab: Another tab logged out, logging out...");
          setUser(null);
          setBackendUser(null);
          clearTimers();
          // Trigger auto-logout to redirect to login page
          handleAutoLogout("Cross-tab logout detected");
        }
      }
    };

    const handleBroadcastMessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case "LOGOUT":
          // Immediate logout sync across all tabs
          console.log("Cross-tab: Received logout broadcast");
          if (user) {
            setUser(null);
            setBackendUser(null);
            clearTimers();
            // Trigger auto-logout to redirect to login page
            handleAutoLogout("Cross-tab logout detected");
          }
          break;

        case "LOGIN":
          // Sync login across tabs
          console.log("Cross-tab: Received login broadcast");
          if (!user) {
            initRef.current = false;
            setIsLoading(true);
          }
          break;

        case "USER_UPDATE":
          // Sync user profile updates
          if (data && user?.id === data.user_id) {
            console.log("Cross-tab: Syncing user profile update");
            setBackendUser((prev) => ({ ...prev, ...data }));
          }
          break;

        case "PREFERENCES_UPDATE":
          // Sync preference changes
          if (data && user) {
            console.log("Cross-tab: Syncing preferences update");
            setPreferences(data);
          }
          break;

        default:
          break;
      }
    };

    // Listen for storage changes from other tabs (fallback for older browsers)
    window.addEventListener("storage", handleStorageChange);

    // Listen for broadcast messages (faster, more reliable)
    channel.addEventListener("message", handleBroadcastMessage);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      channel.removeEventListener("message", handleBroadcastMessage);
      channel.close();
    };
  }, [user, clearTimers, handleAutoLogout]);

  // MSAL events
  useEffect(() => {
    const cbId = instance.addEventCallback?.((e) => {
      try {
        if (e.eventType === EventType.LOGIN_SUCCESS && e.payload?.account) {
          instance.setActiveAccount(e.payload.account);
          AppSession.setLoggedIn();
          setAuthError(null);
          scheduleTokenRefresh(e.payload.account);
          initRef.current = false;
          setIsLoading(true);
        }
        if (e.eventType === EventType.LOGIN_FAILURE) {
          setAuthError(e.error?.message || "Login failed");
          AppSession.clearSession();
        }
        if (e.eventType === EventType.LOGOUT_SUCCESS) {
          AppSession.clearSession();
          setUser(null);
          setBackendUser(null);
          clearTimers();
        }
        if (e.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
          if (
            e.error?.errorCode === "token_renewal_error" ||
            e.error?.errorCode === "user_login_error"
          ) {
            handleAutoLogout("Token acquisition failed");
          }
        }
      } catch (err) {}
    });
    return () => {
      if (cbId) instance.removeEventCallback?.(cbId);
    };
  }, [instance, scheduleTokenRefresh, handleAutoLogout, clearTimers]);

  // Initialize user
  useEffect(() => {
    const init = async () => {
      if (initRef.current) return;
      initRef.current = true;

      try {
        const account =
          instance.getActiveAccount() || instance.getAllAccounts()?.[0];

        if (!AppSession.isLoggedIn() || !account) {
          setIsLoading(false);
          return;
        }

        if (isTokenExpired(account)) {
          handleAutoLogout("Token expired");
          return;
        }

        console.log("Valid session found, initializing user");

        // CRITICAL: Acquire BOTH tokens upfront before setting any state
        // This ensures API calls won't be made without proper authorization
        let graphTokenResp, profile;

        try {
          // Step 1: Get backend API token FIRST (most critical)
          console.log("Acquiring backend API token...");
          const backendResp = await instance.acquireTokenSilent({
            scopes: [
              "api://b0e110d8-17b7-4289-9340-9ed8576c898f/access_as_user",
            ],
            account,
          });

          if (!backendResp?.accessToken) {
            throw new Error("No backend access token received");
          }

          if (!validateTokenAudience(backendResp.accessToken)) {
            throw new Error("Invalid backend token audience");
          }

          console.log("✅ Backend API token acquired successfully");
        } catch (backendError) {
          console.error("Failed to acquire backend API token:", backendError);
          // If backend token fails, try to get it interactively
          if (
            backendError.errorCode === "consent_required" ||
            backendError.errorCode === "interaction_required"
          ) {
            console.log("Consent required, redirecting to login...");
            handleAutoLogout("Consent required for backend API");
            return;
          }
          throw backendError;
        }

        try {
          // Step 2: Get Graph API token for profile (optional, can fail gracefully)
          console.log("Acquiring Microsoft Graph token...");
          graphTokenResp = await instance.acquireTokenSilent({
            scopes: ["User.Read"],
            account,
          });

          // Fetch user profile from Graph API
          const profileRes = await fetch(
            "https://graph.microsoft.com/v1.0/me",
            {
              headers: {
                Authorization: `Bearer ${graphTokenResp.accessToken}`,
              },
            },
          );
          profile = profileRes.ok ? await profileRes.json() : null;
          console.log("✅ User profile fetched from Microsoft Graph");
        } catch (graphError) {
          console.warn(
            "Failed to fetch user profile, using basic info:",
            graphError,
          );
          // Continue with basic profile info from account
          profile = null;
        }

        const azureUserData = {
          name: account.name || profile?.displayName || "User",
          email: account.username || profile?.mail || "",
          id: account.localAccountId,
          tenantId: account.tenantId,
          profile,
          avatarUrl: null,
        };

        // Step 3: Set user state to enable authenticated UI
        setUser(azureUserData);
        scheduleTokenRefresh(account);

        // Step 4: Sync with backend (token is already acquired above)
        console.log("Syncing user with backend...");
        await syncUserWithBackend(azureUserData);
        console.log("✅ User initialization complete");
      } catch (e) {
        console.error("Init failed:", e);
        if (
          e.errorCode === "token_renewal_error" ||
          e.errorCode === "consent_required" ||
          e.errorCode === "interaction_required"
        ) {
          handleAutoLogout("Session expired or consent required");
        } else {
          AppSession.clearSession();
          setUser(null);
        }
        initRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    if ((!user || isLoading) && !initRef.current) init();
  }, [
    instance,
    user,
    isLoading,
    isTokenExpired,
    handleAutoLogout,
    scheduleTokenRefresh,
    syncUserWithBackend,
    validateTokenAudience,
  ]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const login = async () => {
    if (inProgress !== InteractionStatus.None) {
      setAuthError("Auth in progress...");
      setTimeout(() => setAuthError(null), 3000);
      return;
    }

    try {
      setAuthError(null);
      AppSession.setLoggedIn();
      // Request both Graph API and Backend API scopes during login
      await instance.loginRedirect({
        scopes: [
          "User.Read", // For Microsoft Graph profile
          "api://b0e110d8-17b7-4289-9340-9ed8576c898f/access_as_user", // For backend API
        ],
        prompt: "select_account",
      });
    } catch (e) {
      if (e.errorCode !== "interaction_in_progress") {
        AppSession.clearSession();
        setAuthError(e.message || "Login failed");
      }
    }
  };

  const logout = async () => {
    if (inProgress !== InteractionStatus.None) {
      setAuthError("Please wait...");
      setTimeout(() => setAuthError(null), 3000);
      return;
    }

    // Broadcast logout to all tabs immediately
    try {
      const channel = new BroadcastChannel("auth_channel");
      channel.postMessage({ type: "LOGOUT" });
      channel.close();
    } catch (e) {
      console.warn(
        "BroadcastChannel not supported, using localStorage fallback",
      );
    }

    AppSession.clearSession();
    clearTimers();
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const refreshUser = useCallback(() => {
    initRef.current = false;
    setIsLoading(true);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        backendUser,
        preferences,
        isLoading,
        isAuthenticated: !!user && !authError,
        authError,
        login,
        logout,
        getAccessToken,
        refreshUser,
        updatePreferences,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
